import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import RtlLayout from "layouts/rtl";
import AdminLayout from "layouts/admin";
import AuthLayout, {
  AuthSignIn,
  AuthSignUp,
  AuthPasswordRecovery,
} from "layouts/auth";
import ClientLayout from "layouts/client";
import RequireAdmin from "components/auth/RequireAdmin";
import RequireClient from "components/auth/RequireClient";
import OwnerSessionBridge from "views/auth/OwnerSessionBridge";
import AdminOwnerLogin from "views/auth/AdminOwnerLogin";
import ClientDashboard from "views/client/Dashboard";
import ClientSubscription from "views/client/Subscription";
import SubscriptionPath1BrokerPartner from "views/client/SubscriptionPath1BrokerPartner";
import SubscriptionPath2PersonnalBroker from "views/client/SubscriptionPath2PersonnalBroker";
import ClientBilling from "views/client/Billing";
import ClientSupportHelp from "views/client/SupportHelp";
import ClientSuggestions from "views/client/ClientSuggestions";
import ClientSettings from "views/client/Settings";
import ClientReferral from "views/client/ClientReferral";
import ClientContest from "views/client/ClientContest";
import ComingSoon from "views/ComingSoon";
import RiskDisclaimer from "views/RiskDisclaimer";
import PrivacyPolicy from "views/PrivacyPolicy";
import Methodology from "views/Methodology";
import TermsAndConditions from "views/TermsAndConditions";
import RefundPolicy from "views/RefundPolicy";
import HowItWorks from "views/HowItWorks";
import HowItWorksFlow from "views/HowItWorksFlow";
import HIWexample from "views/HIWexample";
import PayUsdt from "views/PayUsdt";
import ExtendSlot from "views/ExtendSlot";
import PaymentPending from "views/PaymentPending";
import BuySlot from "views/BuySlot";
import FAQ from "views/FAQ";
import ReferralContest from "views/contest/ReferralContest";
import Pricing from "views/Pricing";
import AboutUs from "views/AboutUs";
import ContactUs from "views/ContactUs";
import Results from "views/Results";
import PublicSiteLayout from "layouts/PublicSiteLayout";
import Maintenance from "views/Maintenance";
import MaintenanceModeRedirect from "components/MaintenanceModeRedirect";

/**
 * `/` → page Coming Soon (landing publique).
 * `REACT_APP_COMING_SOON=0` : `/` redirige vers l’admin.
 * `/comingsoon` et `/waitlist` redirigent vers `/` (anciens liens).
 */
function RootRoute() {
  const comingSoonOff =
    process.env.REACT_APP_COMING_SOON === "0" ||
    process.env.REACT_APP_COMING_SOON === "false";

  if (comingSoonOff) {
    return <Navigate to="/SignIn" replace />;
  }
  return <ComingSoon />;
}

/** Garde ?promo=…&path=… quand on redirige BuySlot → PayUSDT */
function RedirectWithSearch({ to }) {
  const location = useLocation();
  return (
    <Navigate to={`${to}${location.search}${location.hash}`} replace />
  );
}

const App = () => {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/^\/+/, "/") || "/";
  if (location.pathname !== normalizedPath) {
    return (
      <Navigate
        to={`${normalizedPath}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <>
      <MaintenanceModeRedirect />
      <Routes>
      <Route path="owner-session" element={<OwnerSessionBridge />} />
      <Route path="Maintenance" element={<Maintenance />} />
      <Route
        path="maintenance"
        element={<Navigate to="/Maintenance" replace />}
      />
      {/*
        /owner-login : page HTML admin uniquement (API + proxy dev), pas de route React.
        Les clients ne doivent jamais y être redirigés après déconnexion.
      */}
      <Route path="/" element={<PublicSiteLayout />}>
        <Route index element={<RootRoute />} />
        <Route path="comingsoon" element={<Navigate to="/" replace />} />
        <Route path="Methodology" element={<Methodology />} />
        <Route path="RiskDisclaimer" element={<RiskDisclaimer />} />
        <Route path="PrivacyPolicy" element={<PrivacyPolicy />} />
        <Route path="TermsAndConditions" element={<TermsAndConditions />} />
        <Route path="RefundPolicy" element={<RefundPolicy />} />
        <Route path="methodology" element={<Navigate to="/Methodology" replace />} />
        <Route path="disclaimer" element={<Navigate to="/RiskDisclaimer" replace />} />
        <Route path="privacy-policy" element={<Navigate to="/PrivacyPolicy" replace />} />
        <Route path="terms-and-conditions" element={<Navigate to="/TermsAndConditions" replace />} />
        <Route path="refund-policy" element={<Navigate to="/RefundPolicy" replace />} />
        <Route path="hiw" element={<HowItWorks />} />
        {/* Canonical public URLs (mixed case); kebab-case → redirect */}
        <Route path="AboutUs" element={<AboutUs />} />
        <Route path="Pricing" element={<Pricing />} />
        <Route path="Results" element={<Results />} />
        <Route path="ContactUs" element={<ContactUs />} />
        <Route path="HowItWorks" element={<HowItWorksFlow />} />
        <Route path="HIWexample" element={<HIWexample />} />
        <Route
          path="hiwexample"
          element={<Navigate to="/HIWexample" replace />}
        />
        <Route path="FrequentlyAskedQuestions" element={<FAQ />} />
        <Route path="Contest/RefferalContest" element={<ReferralContest />} />
        <Route
          path="contest/refferalcontest"
          element={<Navigate to="/Contest/RefferalContest" replace />}
        />
        <Route path="about-us" element={<Navigate to="/AboutUs" replace />} />
        <Route path="pricing" element={<Navigate to="/Pricing" replace />} />
        <Route path="results" element={<Navigate to="/Results" replace />} />
        <Route path="contact-us" element={<Navigate to="/ContactUs" replace />} />
        <Route path="how-it-works" element={<Navigate to="/HowItWorks" replace />} />
        <Route path="faq" element={<Navigate to="/FrequentlyAskedQuestions" replace />} />
        <Route path="FAQ" element={<Navigate to="/FrequentlyAskedQuestions" replace />} />
        <Route
          path="QuickSignUp"
          element={<Navigate to="/SignUp" replace />}
        />
        <Route
          path="quick-sign-up"
          element={<Navigate to="/SignUp" replace />}
        />
        <Route path="waitlist" element={<Navigate to="/" replace />} />
      </Route>
      <Route element={<RequireClient />}>
        <Route path="PayUSDT" element={<PayUsdt />} />
        <Route path="ExtendSlot" element={<ExtendSlot />} />
        <Route path="PayUSDT/pending" element={<PaymentPending />} />
        <Route path="PayUsdt" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route path="PayUsdt/pending" element={<PaymentPending />} />
        <Route path="BuySlot" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route path="BuySlot/pending" element={<PaymentPending />} />
        <Route path="pay/usdt" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route
          path="pay/usdt-bsc"
          element={<RedirectWithSearch to="/PayUSDT" />}
        />
      </Route>
      <Route path="SignIn" element={<AuthSignIn />} />
      <Route path="SignUp" element={<AuthSignUp />} />
      <Route path="PasswordRecovery" element={<AuthPasswordRecovery />} />
      <Route path="auth/sign-in" element={<Navigate to="/SignIn" replace />} />
      <Route path="auth/sign-up" element={<Navigate to="/SignUp" replace />} />
      <Route
        path="auth/forgot-password"
        element={<Navigate to="/PasswordRecovery" replace />}
      />
      <Route
        path="forgot-password"
        element={<Navigate to="/PasswordRecovery" replace />}
      />
      <Route path="auth/*" element={<AuthLayout />} />
      <Route
        path="subscriptions/Path1BrokerPartner"
        element={<Navigate to="/client/subscriptions/Path1BrokerPartner" replace />}
      />
      <Route
        path="subscriptions/Path2PersonnalBroker"
        element={<Navigate to="/client/subscriptions/Path2PersonnalBroker" replace />}
      />
      {process.env.NODE_ENV === "development" && (
        <Route
          path="client/preview"
          element={<Navigate to="/client/dashboard" replace />}
        />
      )}
      <Route element={<RequireClient />}>
        <Route path="BuySlotTest" element={<BuySlot />} />
        <Route path="Buy slot" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route path="buy-slot" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route path="pay/path2-slot" element={<RedirectWithSearch to="/PayUSDT" />} />
        <Route
          path="/DashboardUnderProduction"
          element={<Navigate to="/client/dashboard" replace />}
        />
        <Route
          path="dashboard"
          element={<Navigate to="/client/dashboard" replace />}
        />
        <Route
          path="Dashboard"
          element={<Navigate to="/client/dashboard" replace />}
        />
        <Route
          path="dashboardunderproduction"
          element={<Navigate to="/client/dashboard" replace />}
        />
        <Route path="client" element={<ClientLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route
            path="trading-accounts"
            element={<Navigate to="/client/dashboard" replace />}
          />
          <Route path="subscription" element={<ClientSubscription />} />
          <Route
            path="subscriptions/Path1BrokerPartner"
            element={<SubscriptionPath1BrokerPartner />}
          />
          <Route
            path="subscriptions/Path2PersonnalBroker"
            element={<SubscriptionPath2PersonnalBroker />}
          />
          <Route path="billing" element={<ClientBilling />} />
          <Route path="referral" element={<ClientReferral />} />
          <Route path="contest" element={<ClientContest />} />
          <Route
            path="connected-platforms"
            element={<Navigate to="/client/dashboard" replace />}
          />
          <Route
            path="support/suggestions"
            element={<ClientSuggestions />}
          />
          <Route path="support" element={<ClientSupportHelp />} />
          <Route
            path="documents"
            element={<Navigate to="/client/dashboard" replace />}
          />
          <Route
            path="activity-log"
            element={<Navigate to="/client/dashboard" replace />}
          />
          <Route
            path="slots"
            element={<Navigate to="/client/subscription" replace />}
          />
          <Route path="dashboard" element={<ClientDashboard />} />
          <Route path="settings" element={<ClientSettings />} />
        </Route>
      </Route>
      <Route path="admin/login" element={<AdminOwnerLogin />} />
      <Route
        path="admin/*"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      />
      <Route path="rtl/*" element={<RtlLayout />} />
    </Routes>
    </>
  );
};

export default App;
